/**
 * Libellés hiérarchiques des zones et environnements de stockage (SFD Échanges et stockage 1.1).
 *
 * Une liste de sélection qui n'est pas précédée de ses niveaux parents en cascade libelle chaque
 * option avec sa hiérarchie complète : `Entrepôt > Zone` pour une zone,
 * `Entrepôt > Zone > Environnement` pour un environnement. Dans une cascade (entrepôt, puis
 * zone, puis environnement), seul le libellé du niveau est affiché.
 */

const SEPARATOR = " > ";

const join = (parts) => parts.filter((part) => part).join(SEPARATOR);

/**
 * Libellé d'une zone : `Entrepôt > Zone`.
 * @param {object} zone  { name, warehouse: { name } }
 */
export function zoneLabel(zone) {
  if (!zone) {
    return null;
  }
  return join([zone.warehouse && zone.warehouse.name, zone.name]);
}

/**
 * Libellé d'un environnement : `Entrepôt > Zone > Environnement`.
 * Accepte les formes renvoyées par l'API (objets imbriqués ou champs à plat
 * `warehouse_name`, `storage_zone_name`, `storage_environment_name`).
 * @param {object}  environment
 * @param {boolean} withWarehouse  false quand l'entrepôt est implicite à l'écran
 */
export function environmentLabel(environment, withWarehouse = true) {
  if (!environment) {
    return null;
  }
  const warehouse = environment.warehouse_name || (environment.warehouse && environment.warehouse.name);
  const zone = environment.storage_zone_name || (environment.storage_zone && environment.storage_zone.name);
  const name = environment.storage_environment_name || environment.name;

  return join([withWarehouse ? warehouse : null, zone, name]);
}

/**
 * Options d'environnements d'un entrepôt, regroupées par zone (OptionsAutocomplete `grouped`) :
 * l'environnement sous l'intitulé de sa zone, `Zone > Environnement` une fois choisi.
 */
export function environmentGroupedOptions(environments, warehouseId) {
  return (environments || [])
    .filter((environment) => environment.warehouse && environment.warehouse.id == warehouseId)
    .map((environment) => ({
      value: environment.id,
      text: environment.name,
      group: environment.storage_zone && environment.storage_zone.name,
      selectedText: environmentLabel(environment, false),
    }));
}

/**
 * Options de sélection d'environnements hors cascade, libellées avec leur hiérarchie complète,
 * éventuellement limitées à un entrepôt (l'entrepôt est alors omis du libellé).
 */
export function environmentOptions(environments, warehouseId = null) {
  return (environments || [])
    .filter((environment) => !warehouseId || (environment.warehouse && environment.warehouse.id == warehouseId))
    .map((environment) => ({ value: environment.id, text: environmentLabel(environment, !warehouseId) }))
    .sort((a, b) => a.text.localeCompare(b.text));
}



// WEBPACK FOOTER //
// ./src/components/StorageEnvironment/labels.js